import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { type HonestBacktestPosition } from '@/services/HonestBacktestService';

interface HonestBacktestResultsTableProps {
  positions: HonestBacktestPosition[];
  maxRows?: number;
}

export const HonestBacktestResultsTable: React.FC<HonestBacktestResultsTableProps> = ({
  positions,
  maxRows = positions.length // Show all positions by default
}) => {
  const displayPositions = positions;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/40">
            <th className="text-left p-2 font-medium">Position</th>
            <th className="text-left p-2 font-medium">Pattern Used</th>
            <th className="text-left p-2 font-medium">Win Prob</th>
            <th className="text-left p-2 font-medium">Prediction</th>
            <th className="text-left p-2 font-medium">Actual</th>
            <th className="text-left p-2 font-medium">Result</th>
            <th className="text-left p-2 font-medium">Patterns</th>
            <th className="text-left p-2 font-medium">Details</th>
          </tr>
        </thead>
        <tbody>
          {displayPositions.map((position, index) => {
            const isCorrect = position.prediction === 'SKIP' ? null :
              (position.prediction === 'WIN' && position.actualResult === 1) ||
              (position.prediction === 'LOSS' && position.actualResult === 0);

            return (
              <tr key={index} className="border-b border-border/20 hover:bg-muted/5">
                <td className="p-2 font-mono text-xs">{position.position}</td>
                <td className="p-2">
                  {(() => {
                    // Prioritize full patterns (live results)
                    if (position.patternBreakdown?.patternsUsed && position.patternBreakdown.patternsUsed.length > 0) {
                      const patterns = position.patternBreakdown.patternsUsed;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <Badge variant="outline" className="text-xs">
                                  {patterns[0].pattern}
                                  {` (${patterns[0].pattern.length})`}
                                  {patterns.length > 1 && ` +${patterns.length - 1}`}
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md">
                              <div className="space-y-2">
                                <p className="font-medium">Patterns Used (1=Win, 0=Loss):</p>
                                {patterns.map((pattern, i) => (
                                  <div key={i} className="text-xs space-y-1 border-l-2 border-border/40 pl-2">
                                    <p><strong>Pattern:</strong> {pattern.pattern} → {pattern.pattern.split('').map(bit => bit === '1' ? 'W' : 'L').join('')}</p>
                                    <p><strong>Length:</strong> {pattern.pattern.length} trades</p>
                                    <p><strong>Frequency:</strong> {pattern.frequency} occurrences</p>
                                    <p><strong>Success Rate:</strong> {pattern.successRate.toFixed(1)}%</p>
                                    <p><strong>Confidence Score:</strong> {pattern.confidenceScore}/100</p>
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    
                    // Fallback to compact patterns (history results)
                    if (position.patternsSample && position.patternsSample.length > 0) {
                      const patterns = position.patternsSample;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <Badge variant="secondary" className="text-xs">
                                  {patterns[0].pattern}
                                  {` (${patterns[0].pattern.length})`}
                                  {patterns.length > 1 && ` +${patterns.length - 1}`}
                                  <span className="ml-1 text-muted-foreground">(compact)</span>
                                </Badge>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-md">
                              <div className="space-y-2">
                                <p className="font-medium">Top Patterns Used (compact history):</p>
                                {patterns.map((pattern, i) => (
                                  <div key={i} className="text-xs space-y-1 border-l-2 border-border/40 pl-2">
                                    <p><strong>Pattern:</strong> {pattern.pattern} → {pattern.pattern.split('').map(bit => bit === '1' ? 'W' : 'L').join('')}</p>
                                    <p><strong>Length:</strong> {pattern.pattern.length} trades</p>
                                    <p><strong>Frequency:</strong> {pattern.frequency} occurrences</p>
                                    <p><strong>Success Rate:</strong> {pattern.successRate.toFixed(1)}%</p>
                                    <p><strong>Confidence Score:</strong> {pattern.confidenceScore}/100</p>
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    
                    // Final fallback for positions with patterns but no pattern data stored
                    if (position.usedPatterns > 0) {
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-xs opacity-60">
                                {position.usedPatterns} used (compact)
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">Pattern details not available in compact storage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }
                    
                    return <span className="text-muted-foreground text-xs">No patterns</span>;
                  })()}
                </td>
                <td className="p-2">
                  <span className="font-mono text-xs">
                    {position.winProbability?.toFixed(1)}%
                  </span>
                </td>
                <td className="p-2">
                  <Badge 
                    variant={
                      position.prediction === 'WIN' ? 'default' :
                      position.prediction === 'LOSS' ? 'destructive' : 'secondary'
                    }
                    className="text-xs"
                  >
                    {position.prediction}
                  </Badge>
                </td>
                <td className="p-2">
                  <Badge 
                    variant={position.actualResult === 1 ? 'default' : 'destructive'}
                    className="text-xs"
                  >
                    {position.actualResult === 1 ? 'WIN' : 'LOSS'}
                  </Badge>
                </td>
                <td className="p-2">
                  {position.prediction === 'SKIP' ? (
                    <Badge variant="outline" className="text-xs">SKIP</Badge>
                  ) : (
                    <Badge 
                      variant={isCorrect ? 'default' : 'destructive'}
                      className="text-xs"
                    >
                      {isCorrect ? 'CORRECT' : 'WRONG'}
                    </Badge>
                  )}
                </td>
                <td className="p-2">
                  <span className="text-xs text-muted-foreground">
                    {position.currentPatternCount} total
                    {position.usedPatterns > 0 && ` (${position.usedPatterns} used)`}
                  </span>
                </td>
                <td className="p-2">
                  {position.skipReason && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">{position.skipReason}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="text-center py-2 text-xs text-muted-foreground">
        Showing all {positions.length} positions
      </div>
    </div>
  );
};